import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '@little-chef/ui/brand';
import { useAuth } from '../../contexts/AuthContext';
import { uiClasses } from './uiClasses';

function BurgerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

interface AppMenuProps {
  onBeforeNavigate?: () => boolean;
}

export function AppMenu({ onBeforeNavigate }: AppMenuProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { t: tSettings } = useTranslation('settings');

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const proceedNavigation = () => {
    if (!onBeforeNavigate) return true;
    return onBeforeNavigate();
  };

  const isRecipesActive = location.pathname.startsWith('/catalog');
  const isCookbooksActive = location.pathname.startsWith('/cookbooks');
  const isPlannerActive = location.pathname.startsWith('/planner');
  const isIngredientsActive = location.pathname.startsWith('/ingredients');
  const isChallengesActive = location.pathname.startsWith('/challenges');
  const isSettingsActive = location.pathname.startsWith('/settings');

  const goToSettings = () => {
    if (!proceedNavigation()) return;
    setMenuOpen(false);
    navigate('/settings');
  };

  const goToCatalog = () => {
    if (!proceedNavigation()) return;
    setMenuOpen(false);
    navigate('/catalog');
  };

  const goToCookbooks = () => {
    if (!proceedNavigation()) return;
    setMenuOpen(false);
    navigate('/cookbooks');
  };

  const goToPlanner = () => {
    if (!proceedNavigation()) return;
    setMenuOpen(false);
    navigate('/planner');
  };

  const goToIngredients = () => {
    if (!proceedNavigation()) return;
    setMenuOpen(false);
    navigate('/ingredients');
  };

  const goToChallenges = () => {
    if (!proceedNavigation()) return;
    setMenuOpen(false);
    navigate('/challenges');
  };

  const handleSignOut = () => {
    if (!proceedNavigation()) return;
    setMenuOpen(false);
    signOut();
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const navLinks = (
    <>
      <button
        type="button"
        onClick={goToCatalog}
        className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          isRecipesActive
            ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
            : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
        }`}
      >
        {t('recipes')}
      </button>
      <button
        type="button"
        onClick={goToCookbooks}
        className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          isCookbooksActive
            ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
            : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
        }`}
      >
        {t('cookbooks')}
      </button>
      <button
        type="button"
        onClick={goToPlanner}
        className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          isPlannerActive
            ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
            : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
        }`}
      >
        {t('weeklyPlanner')}
      </button>
      <button
        type="button"
        onClick={goToIngredients}
        className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          isIngredientsActive
            ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
            : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
        }`}
      >
        {t('ingredients')}
      </button>
      <button
        type="button"
        onClick={goToChallenges}
        className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          isChallengesActive
            ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
            : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
        }`}
      >
        {t('challenges')}
      </button>
      <button
        type="button"
        onClick={goToSettings}
        className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          isSettingsActive
            ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
            : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
        }`}
      >
        {tSettings('title')}
      </button>
      <button
        type="button"
        onClick={handleSignOut}
        className={`${uiClasses.button.outline} w-full justify-center mt-2`}
      >
        {t('signOut')}
      </button>
    </>
  );

  return (
    <header className="bg-white shadow-sm border-b border-[color:var(--color-border)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={goToCatalog}
            className="flex items-center gap-2 rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-ring)] focus:ring-offset-2 shrink-0"
            aria-label={t('recipes')}
          >
            <BrandLogo className="h-8 w-auto" />
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-2" aria-label="App menu">
            <button
              type="button"
              onClick={goToCatalog}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                isRecipesActive
                  ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
                  : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
              }`}
            >
              {t('recipes')}
            </button>
            <button
              type="button"
              onClick={goToCookbooks}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                isCookbooksActive
                  ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
                  : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
              }`}
            >
              {t('cookbooks')}
            </button>
            <button
              type="button"
              onClick={goToPlanner}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                isPlannerActive
                  ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
                  : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
              }`}
            >
              {t('weeklyPlanner')}
            </button>
            <button
              type="button"
              onClick={goToIngredients}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                isIngredientsActive
                  ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
                  : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
              }`}
            >
              {t('ingredients')}
            </button>
            <button
              type="button"
              onClick={goToChallenges}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                isChallengesActive
                  ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
                  : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
              }`}
            >
              {t('challenges')}
            </button>
            <button
              type="button"
              onClick={goToSettings}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                isSettingsActive
                  ? 'bg-[color:var(--color-primary)] text-[color:var(--color-primary-text)]'
                  : 'text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)]'
              }`}
            >
              {tSettings('title')}
            </button>
            <button
              type="button"
              onClick={handleSignOut}
              className={`${uiClasses.button.outline} ml-2`}
            >
              {t('signOut')}
            </button>
          </nav>

          {/* Mobile: burger + dropdown */}
          <div className="relative md:hidden shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="p-2 rounded-lg text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-ring)] focus:ring-offset-2"
              aria-expanded={menuOpen}
              aria-haspopup="true"
              aria-label={t('recipes')}
            >
              <BurgerIcon className="w-6 h-6" />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-56 py-2 rounded-xl border shadow-lg z-50"
                style={{
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-card)',
                }}
                role="menu"
              >
                {navLinks}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
